import { QueryClient } from '@tanstack/react-query';
import { isConnectionTimeout } from '@tradesmatepro/shared-ui/utils/resilientFetch';

/**
 * Retries happen in ONE layer (ruling 6, PRODUCT_DECISIONS "Data efficiency", 2026-11-02).
 *
 * Every request made through the Supabase client passes through resilientFetch
 * (packages/shared-ui/utils/resilientFetch.js). For READS (GET, and RPCs in its
 * READ_ONLY_RPCS) it retries twice on a timeout, a network failure or a 502/503/504, and it
 * deliberately does NOT retry a statement timeout (57014). Everything else it classifies as a
 * write and never retries (the 2026-10-23 policy) - that includes RPCs not in READ_ONLY_RPCS,
 * such as get_company_jobs_filtered, which are VOLATILE in the database. A React Query retry
 * on top repeated the same failed read again (up to 2 x 3 attempts), re-ran 57014s against an
 * already-overloaded database, and retried "write"-classified RPCs the fetch layer had ruled
 * out. So an error that came back from the Supabase client is not retried here; the rest of
 * those (PostgREST, RLS, constraint errors) would fail the same way a second time.
 *
 * Errors from outside that client (e.g. useEmployeesList's raw fetch() to an edge function,
 * which resilientFetch never sees) keep the single retry they always had.
 */
export const isSupabaseClientError = (error) => {
  if (!error || typeof error !== 'object') return false;
  if (isConnectionTimeout(error)) return true;
  // postgrest-js v2 hands back a PostgrestError, or a PLAIN object parsed from the response
  // body ({ code, message, details, hint }, or { message } for a non-JSON 5xx body).
  if (error.name === 'PostgrestError') return true;
  if (!(error instanceof Error) && 'message' in error) return true;
  // storage-js errors (StorageApiError / StorageUnknownError): storage GETs are already
  // retried by resilientFetch.
  if (/^Storage/.test(String(error.name || ''))) return true;
  // The same failures re-thrown as `new Error(error.message)` by a hook.
  const msg = String(error.message || '');
  return /canceling statement due to statement timeout|^TypeError: (Failed to fetch|NetworkError|Load failed)/.test(msg);
};

export const shouldRetryQuery = (failureCount, error) => failureCount < 1 && !isSupabaseClientError(error);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes — saves DB reads during normal browsing
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
      retry: shouldRetryQuery, // one retry layer only — see shouldRetryQuery above
      // Ruling 1 (PRODUCT_DECISIONS "Data efficiency", 2026-11-02): returning to the tab reuses
      // data under staleTime (5 min) old; older data refetches. Was 'always' (every focus). Live
      // surfaces on the refresh map (Messages, Team Chat, bell, marketplace) use realtime; the
      // Calendar backlog and /scheduling keep 'always' per query (useCalendarData.js,
      // useSchedulingData.js); Reports/Employees opt out (false in their hooks).
      refetchOnWindowFocus: true,
      refetchOnReconnect: true, // Refetch on network reconnect
      refetchInterval: false, // No automatic polling (app actions invalidate cache directly)
    },
  },
});
